'use client';
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, Check, Loader2, Info, GripVertical } from 'lucide-react';
import ConfirmModal from '@/components/ui/ConfirmModal';

interface PriceItem {
  id: string;
  serviceType: string;
  baseFee: number;
  perUnitRate: number;
  unit: string;
  minCharge: number;
  notes: string;
}

const initialPriceItems: PriceItem[] = [
  { id: 'price-001', serviceType: 'Interior Painting', baseFee: 400, perUnitRate: 2.5, unit: 'sqft', minCharge: 400, notes: 'Standard walls only. Ceilings +20%' },
  { id: 'price-002', serviceType: 'Exterior Painting', baseFee: 800, perUnitRate: 3.5, unit: 'sqft', minCharge: 800, notes: 'Includes primer coat. Trim +$150' },
  { id: 'price-003', serviceType: 'Pressure Washing', baseFee: 150, perUnitRate: 0.15, unit: 'sqft', minCharge: 150, notes: 'Residential driveways and patios' },
  { id: 'price-004', serviceType: 'Flooring Installation', baseFee: 500, perUnitRate: 4.0, unit: 'sqft', minCharge: 500, notes: 'LVP and hardwood. Subfloor prep extra' },
  { id: 'price-005', serviceType: 'Drywall Repair', baseFee: 200, perUnitRate: 1.5, unit: 'sqft', minCharge: 200, notes: 'Patching up to 12" holes. Texture matching included' },
  { id: 'price-006', serviceType: 'General Handyman', baseFee: 250, perUnitRate: 85, unit: 'hr', minCharge: 250, notes: '3 hour minimum. Materials billed separately' },
];

interface PriceFormData {
  serviceType: string;
  baseFee: string;
  perUnitRate: string;
  unit: string;
  minCharge: string;
  notes: string;
}

const emptyForm: PriceFormData = {
  serviceType: '',
  baseFee: '',
  perUnitRate: '',
  unit: 'sqft',
  minCharge: '',
  notes: '',
};

export default function PriceTableSettings() {
  const [items, setItems] = useState<PriceItem[]>(initialPriceItems);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState<PriceFormData>(emptyForm);
  const [formErrors, setFormErrors] = useState<Partial<PriceFormData>>({});
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const validateForm = (): boolean => {
    const errors: Partial<PriceFormData> = {};
    if (!formData.serviceType.trim()) errors.serviceType = 'Service name is required';
    if (!formData.baseFee || isNaN(Number(formData.baseFee))) errors.baseFee = 'Valid base fee required';
    if (!formData.perUnitRate || isNaN(Number(formData.perUnitRate))) errors.perUnitRate = 'Valid rate required';
    if (!formData.minCharge || isNaN(Number(formData.minCharge))) errors.minCharge = 'Valid minimum required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveNew = async () => {
    if (!validateForm()) return;
    setIsSaving(true);
    // TODO: POST /api/price-table — insert into Supabase price_table table
    await new Promise((r) => setTimeout(r, 800));
    const newItem: PriceItem = {
      id: `price-${Date.now()}`,
      serviceType: formData.serviceType,
      baseFee: Number(formData.baseFee),
      perUnitRate: Number(formData.perUnitRate),
      unit: formData.unit,
      minCharge: Number(formData.minCharge),
      notes: formData.notes,
    };
    setItems((prev) => [...prev, newItem]);
    setFormData(emptyForm);
    setShowAddForm(false);
    setIsSaving(false);
    toast.success('Price item added successfully');
  };

  const handleStartEdit = (item: PriceItem) => {
    setEditingId(item.id);
    setFormData({
      serviceType: item.serviceType,
      baseFee: String(item.baseFee),
      perUnitRate: String(item.perUnitRate),
      unit: item.unit,
      minCharge: String(item.minCharge),
      notes: item.notes,
    });
    setFormErrors({});
  };

  const handleSaveEdit = async () => {
    if (!validateForm()) return;
    setIsSaving(true);
    // TODO: PATCH /api/price-table/:id — update Supabase price_table row
    await new Promise((r) => setTimeout(r, 600));
    setItems((prev) =>
      prev.map((item) =>
        item.id === editingId
          ? {
              ...item,
              serviceType: formData.serviceType,
              baseFee: Number(formData.baseFee),
              perUnitRate: Number(formData.perUnitRate),
              unit: formData.unit,
              minCharge: Number(formData.minCharge),
              notes: formData.notes,
            }
          : item
      )
    );
    setEditingId(null);
    setFormData(emptyForm);
    setIsSaving(false);
    toast.success('Price item updated');
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    // TODO: DELETE /api/price-table/:id — remove from Supabase price_table
    setItems((prev) => prev.filter((i) => i.id !== deleteTarget));
    setDeleteTarget(null);
    toast.success('Price item removed');
  };

  const unitOptions = ['sqft', 'hr', 'unit', 'room', 'linear ft', 'job'];

  const FormRow = ({ isNew }: { isNew: boolean }) => (
    <div className="p-4 bg-blue-600/5 border border-blue-600/20 rounded-xl space-y-4">
      <p className="text-blue-300 text-xs font-600 uppercase tracking-wide">{isNew ? 'New Service' : 'Edit Service'}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="leverage-label">Service Type *</label>
          <input
            value={formData.serviceType}
            onChange={(e) => setFormData((f) => ({ ...f, serviceType: e.target.value }))}
            placeholder="e.g. Interior Painting — Standard Room"
            className="leverage-input"
          />
          {formErrors.serviceType && <p className="text-red-400 text-xs mt-1">{formErrors.serviceType}</p>}
        </div>
        <div>
          <label className="leverage-label">Base Fee ($) *</label>
          <input
            value={formData.baseFee}
            onChange={(e) => setFormData((f) => ({ ...f, baseFee: e.target.value }))}
            placeholder="400"
            type="number"
            min="0"
            className="leverage-input"
          />
          {formErrors.baseFee && <p className="text-red-400 text-xs mt-1">{formErrors.baseFee}</p>}
        </div>
        <div>
          <label className="leverage-label">Per Unit Rate ($) *</label>
          <div className="flex gap-2">
            <input
              value={formData.perUnitRate}
              onChange={(e) => setFormData((f) => ({ ...f, perUnitRate: e.target.value }))}
              placeholder="2.50"
              type="number"
              min="0"
              step="0.01"
              className="leverage-input flex-1"
            />
            <select
              value={formData.unit}
              onChange={(e) => setFormData((f) => ({ ...f, unit: e.target.value }))}
              className="bg-[#0D0D0D] border border-[#2A2A2A] rounded-lg px-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 w-28 flex-shrink-0"
            >
              {unitOptions.map((u) => <option key={`unit-${u}`} value={u}>{u}</option>)}
            </select>
          </div>
          {formErrors.perUnitRate && <p className="text-red-400 text-xs mt-1">{formErrors.perUnitRate}</p>}
        </div>
        <div>
          <label className="leverage-label">Minimum Charge ($) *</label>
          <input
            value={formData.minCharge}
            onChange={(e) => setFormData((f) => ({ ...f, minCharge: e.target.value }))}
            placeholder="400"
            type="number"
            min="0"
            className="leverage-input"
          />
          {formErrors.minCharge && <p className="text-red-400 text-xs mt-1">{formErrors.minCharge}</p>}
        </div>
        <div>
          <label className="leverage-label">Internal Notes <span className="text-[#444] normal-case">(optional)</span></label>
          <input
            value={formData.notes}
            onChange={(e) => setFormData((f) => ({ ...f, notes: e.target.value }))}
            placeholder="Ceilings extra, trim included, etc."
            className="leverage-input"
          />
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <button
          onClick={() => { setShowAddForm(false); setEditingId(null); setFormData(emptyForm); setFormErrors({}); }}
          className="leverage-btn-secondary text-sm py-2 px-4"
        >
          <X size={14} className="inline mr-1.5" />
          Cancel
        </button>
        <button
          onClick={isNew ? handleSaveNew : handleSaveEdit}
          disabled={isSaving}
          className="leverage-btn-primary text-sm py-2 px-5 flex items-center gap-2"
          style={{ minWidth: '100px' }}
        >
          {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          {isSaving ? 'Saving...' : isNew ? 'Add Service' : 'Save Changes'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-5 max-w-screen-lg">
      {/* Info banner */}
      <div className="flex items-start gap-3 p-4 bg-[#0D0D0D] border border-[#1F1F1F] rounded-xl">
        <Info size={15} className="text-blue-400 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-white text-sm font-600 mb-0.5">How the AI uses your Price Table</p>
          <p className="text-[#666] text-sm leading-relaxed">
            When a client submits a quote, the AI matches the project type to your price table, estimates square footage from the photo, and calculates: <span className="text-[#AAA] font-500">Base Fee + (Per Unit Rate × Estimated Units)</span>. The result is the rough estimate shown to the client and logged in your lead inbox.
          </p>
        </div>
      </div>

      {/* Table */}
      <div className="leverage-card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1F1F1F]">
          <div>
            <h3 className="text-white font-700 text-base">Service Price Table</h3>
            <p className="text-[#555] text-xs mt-0.5">{items.length} services configured</p>
          </div>
          <button
            onClick={() => { setShowAddForm(true); setEditingId(null); setFormData(emptyForm); setFormErrors({}); }}
            className="leverage-btn-primary text-sm py-2 px-4 flex items-center gap-1.5"
          >
            <Plus size={15} />
            Add Service
          </button>
        </div>

        {showAddForm && (
          <div className="p-4 border-b border-[#1F1F1F]">
            <FormRow isNew={true} />
          </div>
        )}

        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm min-w-[700px]">
            <thead>
              <tr className="border-b border-[#1F1F1F]">
                <th className="w-8 px-4 py-3" />
                <th className="px-4 py-3 text-left text-[11px] font-600 text-[#555] uppercase tracking-wider">Service Type</th>
                <th className="px-4 py-3 text-left text-[11px] font-600 text-[#555] uppercase tracking-wider">Base Fee</th>
                <th className="px-4 py-3 text-left text-[11px] font-600 text-[#555] uppercase tracking-wider">Per Unit Rate</th>
                <th className="px-4 py-3 text-left text-[11px] font-600 text-[#555] uppercase tracking-wider">Minimum</th>
                <th className="px-4 py-3 text-left text-[11px] font-600 text-[#555] uppercase tracking-wider">Notes</th>
                <th className="px-4 py-3 text-left text-[11px] font-600 text-[#555] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <React.Fragment key={item.id}>
                  <tr className={`border-b border-[#0F0F0F] hover:bg-[#141414] transition-colors group ${editingId === item.id ? 'bg-blue-600/5' : ''}`}>
                    <td className="px-4 py-3">
                      <GripVertical size={14} className="text-[#333] cursor-grab" />
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-white font-600 text-sm">{item.serviceType}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-amber-400 font-700 tabular-nums">${item.baseFee.toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[#CCC] tabular-nums">${item.perUnitRate}/{item.unit}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[#CCC] tabular-nums">${item.minCharge.toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[#666] text-xs truncate max-w-[160px] block">{item.notes || '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleStartEdit(item)}
                          title="Edit this price item"
                          className="w-7 h-7 rounded-lg bg-[#1A1A1A] hover:bg-blue-600/20 hover:text-blue-400 text-[#666] flex items-center justify-center transition-colors"
                        >
                          <Pencil size={12} />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(item.id)}
                          title="Remove this price item — cannot be undone"
                          className="w-7 h-7 rounded-lg bg-[#1A1A1A] hover:bg-red-500/20 hover:text-red-400 text-[#666] flex items-center justify-center transition-colors"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {editingId === item.id && (
                    <tr key={`edit-row-${item.id}`}>
                      <td colSpan={7} className="px-4 py-4 border-b border-[#1F1F1F]">
                        <FormRow isNew={false} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Remove Price Item"
        description="This service will be removed from your price table. Existing estimates already sent to clients won't be affected, but new quotes won't include this service type."
        confirmLabel="Remove Service"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        variant="danger"
      />
    </div>
  );
}